/*
 * Copyright (c) 2025. Sergey S. Chernov - All Rights Reserved
 *
 *  You may use, distribute and modify this code under the
 * terms of the private license, which you must obtain from the author
 *
 * To obtain the license, contact the author: https://t.me/real_sergeych or email to
 * real dot sergeych at gmail.
 */

package net.sergeych.crypto2

import com.ionspin.kotlin.crypto.pwhash.*
import kotlinx.serialization.SerialName
import kotlinx.serialization.Serializable
import net.sergeych.bipack.Unsigned
import net.sergeych.crypto2.KDF.Argon.Alg.V2id_13
import net.sergeych.crypto2.KDF.Complexity.*
import net.sergeych.crypto2.PBKD.Params

@Serializable
sealed class KDF {

    /**
     * KDF complexity: how many resources (CPY and RAM) it requires to transform a string to a key.
     *
     * Constants started with `Fixed...`, e.g., [FixedLow], [FixedMedium] and [FixedHigher]
     * are first clas constants not to be changed in future but can become obsolete (too simple) with the
     * development of the computer hardware. Other constants can change in future releases, so it is critical
     * to serialize [KDF] or [PBKD.Params] generated with such constants and restore them before
     * deriving keys.
     */
    enum class Complexity {
        FixedLow,
        Interactive,
        FixedMedium,
        Moderate,
        FixedHigher,
        Sensitive,
        ;

        /**
         * Create [KDF] of the corresponding strength suitable to derive [numberOfKeys] symmetric keys.
         *
         * Random salt of proper size is used by default. If a custom [salt] size is not [Argon.saltSize],
         * it is deterministically normalized with `Hash.Blake2b.deriveSaltFormBytes(...)`.
         */
        fun kdfForSize(numberOfKeys: Int, salt: UByteArray = Argon.randomSalt()): KDF =
            createDefault(SymmetricKey.keyLength * numberOfKeys, this, salt)

        /**
         * Create [KDF] for deriving [sizeInBytes] bytes using the provided [salt].
         *
         * The [salt] is used as is when its size is [Argon.saltSize]. Otherwise, it is deterministically
         * normalized with `Hash.Blake2b.deriveSaltFormBytes(...)` to the required size.
         */
        fun kdfForSizeInBytes(sizeInBytes: Int, salt: UByteArray): KDF =
            createDefault(sizeInBytes, this, salt)


        /**
         * Derive multiple keys from the password. Derivation params will be included in the key ids, see
         * [SymmetricKey.id] as [KeyId.kdp].

         * If the [salt] size is not [Argon.saltSize], it is deterministically normalized with
         * `Hash.Blake2b.deriveSaltFormBytes(...)`.
         *
         * ___Important: symmetric keys do not save key ids___. _Container do it, so it is possible to re-derive
         * key to open the container, but in many cases you might need to save [KeyId.kdp] separately_.
         * Having no [PBKD.Params] it would not be possible to recreate the key, as complexity defaults tend
         * to change with time.
         */
        @Suppress("unused")
        fun deriveMultiple(password: String, count: Int, salt: UByteArray): List<SymmetricKey> =
            kdfForSize(count, salt).deriveMultipleKeys(password, count)

        /**
         * Derive single key from password, same as [deriveMultiple] with count=1.
         */
        fun derive(password: String, salt: UByteArray): SymmetricKey = deriveMultiple(password, 1, salt).first()

        /**
         * Derive bytes from bytes using the default [Argon] KDF with this complexity.
         *
         * If the [salt] size is not [Argon.saltSize], it is deterministically normalized with
         * `Hash.Blake2b.deriveSaltFormBytes(...)`.
         */
        fun derive(source: UByteArray, salt: UByteArray, derivedSize: Int): UByteArray =
            createDefault(derivedSize, this, salt).deriveFromBytes(source)
    }

    /**
     * Derive a single key from the password, same as [deriveMultipleKeys] with `count==1`
     */
    abstract fun deriveKey(password: String): UByteArray

    /**
     * Derive bytes from bytes using this KDF parameters. The derived byte count is this KDF's configured key size.
     */
    abstract fun deriveFromBytes(source: UByteArray): UByteArray

    /**
     * Derive keys from lower part of bytes derived from the password. E.g., if generated size is longer than
     * required to create [count] keys, the first bytes will be used. It let use rest bytes for other purposes.
     */
    fun deriveMultipleKeys(password: String, count: Int): List<SymmetricKey> {
        val bytes = deriveKey(password)
        val ks = SymmetricKey.keyLength
        check(ks * count <= bytes.size) { "KDF is too short for $count keys: ${bytes.size} we need ${ks * count}" }
        return (0..<count).map {
            val base = it * ks
            Params(this, base, ks).deriveKey(password)
        }
    }

    @Serializable
    @SerialName("argon")
    data class Argon(
        val algorithm: Alg,
        @Unsigned
        val instructionsComplexity: ULong,
        @Unsigned
        val memComplexity: Int,
        val salt: UByteArray,
        @Unsigned
        val keySize: Int,
    ) : KDF(), Comparable<Argon> {


        /**
         * Very abstract strength comparison. If a1 > a2 it generally means that its complexity, and, hopefully,
         * strength is higher.
         */
        override fun compareTo(other: Argon): Int {
            var d = algorithm.ordinal.compareTo(other.algorithm.ordinal)
            if (d != 0) return d
            d = instructionsComplexity.compareTo(other.instructionsComplexity)
            if (d != 0) return d
            d = memComplexity.compareTo(other.memComplexity)
            if (d != 0) return d
            d = keySize
            if (d != 0) return d
            return 0
        }

        enum class Alg(val code: Int) {
            @Suppress("EnumEntryName", "unused")
            V2i_13(crypto_pwhash_argon2i_ALG_ARGON2I13),
            V2id_13(crypto_pwhash_argon2id_ALG_ARGON2ID13),
            ;

            companion object {
                val default = V2id_13
            }
        }

        override fun deriveKey(password: String): UByteArray =
            PasswordHash.pwhash(keySize, password, salt, instructionsComplexity, memComplexity, algorithm.code)

        override fun deriveFromBytes(source: UByteArray): UByteArray {
            return PasswordHash.pwhash(
                keySize,
                source.encodeToBase64Url(),
                salt,
                instructionsComplexity,
                memComplexity,
                algorithm.code
            )
        }

        override fun equals(other: Any?): Boolean {
            if (this === other) return true
            if (other !is Argon) return false

            if (algorithm != other.algorithm) return false
            if (instructionsComplexity != other.instructionsComplexity) return false
            if (memComplexity != other.memComplexity) return false
            if (keySize != other.keySize) return false
            return salt contentEquals other.salt
        }

        override fun hashCode(): Int {
            var result = algorithm.hashCode()
            result = 31 * result + instructionsComplexity.hashCode()
            result = 31 * result + memComplexity
            result = 31 * result + salt.contentHashCode()
            result = 31 * result + keySize
            return result
        }


        companion object {
            val saltSize: Int = crypto_pwhash_SALTBYTES
            val minKeySize: Int = crypto_pwhash_BYTES_MIN.toInt()

            fun randomSalt() = randomUBytes(saltSize)

            /**
             * Create a deterministic salt suitable fot this KDF from a given text.
             *
             * We recommend to use random salts stored, and [KeyId] of password-generated keys already
             * do it for you. Use this method only if you can't store [KeyId] or salt; it is generally less secure:
             * knowing the base text it is possible to understand, for example, that the same derived password was used
             * more than once (random salt makes it impossible).
             */
            @Suppress("unused")
            fun deriveSaltFromString(text: String) = Hash.Blake2b.deriveSalt(text, saltSize)

            fun create(complexity: Complexity, salt: UByteArray, keySize: Int): Argon {
                require(salt.size == saltSize) { "The salt size should be $saltSize" }
                require(keySize >= minKeySize) { "The key size should be at least $minKeySize bytes" }
                return when (complexity) {
                    FixedLow -> Argon(
                        V2id_13,
                        2UL,
                        67108864,
                        salt, keySize
                    )

                    Interactive -> Argon(
                        Alg.default,
                        crypto_pwhash_OPSLIMIT_INTERACTIVE.toULong(),
                        crypto_pwhash_MEMLIMIT_INTERACTIVE,
                        salt, keySize
                    )

                    FixedMedium -> Argon(
                        V2id_13,
                        3UL,
                        268435456,
                        salt, keySize
                    )

                    Moderate -> Argon(
                        Alg.default,
                        crypto_pwhash_OPSLIMIT_MODERATE,
                        crypto_pwhash_MEMLIMIT_MODERATE,
                        salt, keySize
                    )

                    Sensitive -> Argon(
                        Alg.default,
                        crypto_pwhash_OPSLIMIT_SENSITIVE,
                        crypto_pwhash_MEMLIMIT_SENSITIVE,
                        salt, keySize
                    )

                    FixedHigher -> Argon(
                        V2id_13,
                        4UL,
                        1073741824,
                        salt, keySize
                    )

                }
            }
        }
    }

    /**
     * Fast HKDF derivation for already strong byte material, implemented as described in
     * [RFC 5869](https://www.rfc-editor.org/rfc/rfc5869).
     *
     * HKDF is suitable when [deriveFromBytes] receives source bytes that already contain enough entropy, for
     * example a shared secret produced by a key agreement protocol, random master key material, or another
     * high-entropy secret. It is fast by design: it does not slow down brute-force attempts and does not use
     * extra memory. Do not use it for passwords, memorable phrases, short PINs, user-entered secrets, or other
     * low-entropy inputs; use [Argon] for those instead.
     *
     * The derivation is deterministic for the tuple `(hash, salt, info, keySize, source)`. Store or recreate the
     * same parameters to derive the same output again. [salt] and [info] are not secret, but changing either one
     * changes the derived bytes.
     *
     * @property hash Hash function to use inside HMAC. The default is [Hash.Sha3_256]. [Hash.Blake2b],
     * [Hash.Sha3_256], and [Hash.Sha3_384] are supported; [Hash.Sha3AndBlake] is intentionally rejected because
     * it is a synthetic concatenated hash and has no well-defined HMAC block size.
     * @property salt Optional extraction salt. Prefer a stable random salt when one is available and can be stored
     * with the derived data. An empty salt is allowed by HKDF and is treated as a zero-filled salt of the selected
     * hash output size.
     * @property info Optional application-specific context string or binary label. Use it to domain-separate
     * outputs derived from the same source, for example `"container encryption"`, `"header auth"`, or a protocol
     * transcript hash. It may be empty when there is only one unambiguous use for the derived bytes.
     * @property keySize Number of bytes to derive. It must be positive and no larger than `255 * hashLen`, where
     * `hashLen` is the output size of [hash].
     */
    @Serializable
    @SerialName("hkdf")
    data class HKDF(
        val hash: Hash = Hash.Sha3_256,
        val salt: UByteArray = ubyteArrayOf(),
        val info: UByteArray = ubyteArrayOf(),
        @Unsigned
        val keySize: Int,
    ) : KDF() {

        init {
            require(keySize > 0) { "HKDF key size should be positive" }
            require(hash.supportsHmac) { "Hash $hash is not supported for HKDF" }
            require(keySize <= hash.hkdfMaxOutputSize) {
                "HKDF output for $hash is limited to ${hash.hkdfMaxOutputSize} bytes"
            }
        }

        override fun deriveKey(password: String): UByteArray =
            throw UnsupportedOperationException("HKDF is not a password KDF; use Argon for passwords")

        override fun deriveFromBytes(source: UByteArray): UByteArray =
            hkdf(hash, source, salt, info, keySize)

        override fun equals(other: Any?): Boolean {
            if (this === other) return true
            if (other !is HKDF) return false

            if (hash != other.hash) return false
            if (keySize != other.keySize) return false
            if (!salt.contentEquals(other.salt)) return false
            return info.contentEquals(other.info)
        }

        override fun hashCode(): Int {
            var result = hash.hashCode()
            result = 31 * result + salt.contentHashCode()
            result = 31 * result + info.contentHashCode()
            result = 31 * result + keySize
            return result
        }
    }

    companion object {

        /**
         * Create default [Argon] KDF parameters. If [salt] is not exactly [Argon.saltSize], it is
         * deterministically normalized with [Hash.Blake2b.deriveSaltFormBytes] before creating the KDF.
         */
        fun createDefault(keySize: Int, complexity: Complexity, salt: UByteArray = Argon.randomSalt()): KDF {
            val normalizedSalt = if (salt.size == Argon.saltSize) {
                salt
            } else {
                Hash.Blake2b.deriveSaltFormBytes(salt, Argon.saltSize)
            }
            return Argon.create(complexity, normalizedSalt, keySize)
        }

        @Deprecated("Use createDefault", ReplaceWith("createDefault(keySize, complexity, salt)"))
        fun creteDefault(keySize: Int, complexity: Complexity, salt: UByteArray = Argon.randomSalt()): KDF =
            createDefault(keySize, complexity, salt)

    }

    data class Instance(val kdf: KDF, val password: String)

}
